import { IRow } from '../../../../redux/slices/oldBuilder';
import {
  IDataTableExecuteInput,
  MeasureType,
} from '../../../apollo/graphqlSchema';
import {
  prepareCompoundMeasureInput,
  prepareRollingControlInput,
  prepareSimpleMeasureInput,
  prepareSimpleMeasurePropertiesInput,
} from './utils';
import { assertNotNil } from '../../../types';
import { prepareMeasureFilterInput } from './prepareMeasureFilterInput';

/**
 * A function will prepare measure and measuresProperties for Data Table execution.
 *
 * By design, we can have an array of:
 * 1). Simple measures;
 * 2). Named Simple measures;
 * 3). Named Compound measures;
 */
type IPreparedDataTableExecuteInput = Pick<
  IDataTableExecuteInput,
  'measures' | 'measuresProperties'
>;

export const prepareDataTableExecuteMeasureInput = (
  dataTableMeasures: IRow[],
  schemaId: string,
): IPreparedDataTableExecuteInput => {
  return dataTableMeasures.reduce<IPreparedDataTableExecuteInput>(
    (acc, row, rowIndex) => {
      const { defaultNamedMeasure } = row;

      // Parse named compound measure
      if (
        defaultNamedMeasure &&
        defaultNamedMeasure.__typename === 'CompoundMeasure'
      ) {
        const { measures, version, rollingControl } = defaultNamedMeasure;

        assertNotNil(version?.versionId);

        acc.measures.push({
          compound: prepareCompoundMeasureInput({
            measures: [],
            versionId: version?.versionId,
            measureFormula: null, // not supported in Data Table,
            schemaId,
          }),
          rollingControl,
          simple: null,
          type: MeasureType.Compound,
          format: null,
        });
        acc.measuresProperties.push({
          compound: {
            measures: measures.map(() =>
              prepareSimpleMeasurePropertiesInput(row, schemaId),
            ),
            resultFilter: prepareMeasureFilterInput(row.measureFilter),
          },
          simple: null,
          type: MeasureType.Compound,
        });
      } else {
        // Parse basic/named simple measure
        acc.measures.push({
          compound: null,
          rollingControl: prepareRollingControlInput(
            row?.measureRollingControl,
          ),
          simple: prepareSimpleMeasureInput(
            {
              row,
              rowIndex,
              measureFormula: null,
            },
            schemaId,
          ),
          type: MeasureType.Simple,
          format: null,
        });
        acc.measuresProperties.push({
          compound: null,
          simple: prepareSimpleMeasurePropertiesInput(row, schemaId),
          type: MeasureType.Simple,
        });
      }

      return acc;
    },
    {
      measures: [],
      measuresProperties: [],
    },
  );
};
